import React, { FC } from 'react';
import { Trans } from 'react-i18next';

import { RoutePath } from 'routes/route-path.constant';

import { useBackgroundImageService } from 'hooks/use-background-image-service.hook';
import { useLocalStorage } from 'hooks/use-local-storage.hook';

import { Typography } from 'components/typography';

import { TypographyVariants } from 'constants/typography-variants';
import { LocalStorageKeys } from 'constants/local-storage';

import { useStyles } from './styles';

interface CartProps {
    isDesktop: boolean;
    isDiscountApplicable: boolean;
}

export const Cart: FC<CartProps> = ({ isDesktop, isDiscountApplicable }) => {
    const {
        cartWrapper,
        headerInfoWrapper,
        cartTitle,
        divider,
        borderTop,
        infoBlockWithImage,
        infoBlock,
        title,
        subtitle,
        blockTitle,
        boldTitle,
        price,
        fullPrice,
        period,
        total,
        lightGreen,
        dividerContent,
        withoutBorder,
        onlyTopPadding
    } = useStyles();

    const { pageValue: plan } = useLocalStorage({
        key: LocalStorageKeys[RoutePath.Medication],
        defaultValue: {}
    });

    const items = ['svg/checkout/cart/cart-icon.svg', 'webp/checkout/cart/medications.webp'];
    const pageImages = useBackgroundImageService(items);

    const subscriptionTotalPrice = `$${plan.price / 100}`;
    const todayPrice = `$${plan.effectivePrice / 100}`;

    const priceDiff = (plan.price - plan.effectivePrice) / 100;
    const savingPercentage = plan?.discountAmount;

    return (
        <div className={`${cartWrapper} ${isDiscountApplicable ? onlyTopPadding : ''}`}>
            {isDesktop && <div className={headerInfoWrapper}>
                <img src={pageImages[0]} alt='Cart' />
                <Typography variant={TypographyVariants.h2} className={cartTitle}>
                    <Trans i18nKey='checkout-cart-summary' />
                </Typography>
            </div>}

            <div>
                <div className={`${divider} ${isDesktop ? borderTop : ''} ${isDiscountApplicable ? withoutBorder : ''}`}>
                    {isDiscountApplicable ?
                        <div className={dividerContent}>
                            <div className={infoBlockWithImage}>
                                <img src={pageImages[1]} alt='Medications'/>
                                <div className={infoBlock}>
                                    <Typography variant={TypographyVariants.h3} className={title}>
                                        <Trans i18nKey='checkout-cart-details-plan-title'/> ({plan.title})
                                    </Typography>
                                    <Typography variant={TypographyVariants.h3} className={subtitle}>
                                        <Trans i18nKey='checkout-cart-details-monthly-membership'/>
                                    </Typography>
                                    <Typography variant={TypographyVariants.h3} className={subtitle}>
                                        <Trans i18nKey='checkout-cart-details-medication-cost'/>
                                    </Typography>
                                </div>
                            </div>
                            <Typography variant={TypographyVariants.h3} className={`${price} ${fullPrice}`}>
                                {subscriptionTotalPrice}<span className={period}>/mo</span>
                            </Typography>
                        </div>
                        :
                        <>
                            <div className={infoBlockWithImage}>
                                <img src={pageImages[1]} alt='Medications'/>
                                <div className={infoBlock}>
                                    <Typography variant={TypographyVariants.h3} className={title}>
                                        <Trans i18nKey='checkout-cart-details-plan-title'/> ({plan.title})
                                    </Typography>
                                    <Typography variant={TypographyVariants.h3} className={subtitle}>
                                        <Trans i18nKey='checkout-cart-details-monthly-membership'/>
                                    </Typography>
                                    <Typography variant={TypographyVariants.h3} className={subtitle}>
                                        <Trans i18nKey='checkout-cart-details-medication-cost'/>
                                    </Typography>
                                </div>
                            </div>
                            <Typography variant={TypographyVariants.h3} className={`${price} ${fullPrice}`}>
                                {subscriptionTotalPrice}<span className={period}>/mo</span>
                            </Typography>
                        </>
                    }
                </div>
                {isDiscountApplicable && <div className={`${divider} ${lightGreen} ${withoutBorder}`}>
                    <div className={dividerContent}>
                        <Typography variant={TypographyVariants.h3} className={title}>
                            <Trans i18nKey='checkout-cart-details-saving-percentage' values={{ savingPercentage }}/>
                        </Typography>
                        <Typography variant={TypographyVariants.h3} className={`${price} ${fullPrice}`}>
                            -${priceDiff}<span>/mo</span>
                        </Typography>
                    </div>
                </div>}
                <div className={divider}>
                    {isDiscountApplicable ?
                        <div className={dividerContent}>
                            <Typography variant={TypographyVariants.h3} className={`${blockTitle} ${boldTitle}`}>
                                <Trans i18nKey='checkout-cart-details-total'/>
                            </Typography>
                            <Typography variant={TypographyVariants.h3} className={`${price} ${total}`}>
                                {todayPrice}
                            </Typography>
                        </div>
                        :
                        <>
                            <Typography variant={TypographyVariants.h3} className={`${blockTitle} ${boldTitle}`}>
                                <Trans i18nKey='checkout-cart-details-total'/>
                            </Typography>
                            <Typography variant={TypographyVariants.h3} className={`${price} ${total}`}>
                                {todayPrice}
                            </Typography>
                        </>
                    }
                </div>
            </div>
        </div>
    );
};
